import type { AnimationPreset, EasingName, ResolvedFrame, Take, Vec3 } from "../js/simulador/video/take-types"

// Funções de easing (idênticas ao script original)
export const easingFunctions: Record<string, (t: number) => number> = {
  linear:         (t) => t,
  easeIn:         (t) => t * t,
  easeOut:        (t) => t * (2 - t),
  easeInOut:      (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
  easeInCubic:    (t) => t * t * t,
  easeOutCubic:   (t) => --t * t * t + 1,
  easeInOutCubic: (t) => (t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1),
  easeInOutQuart: (t) => (t < 0.5 ? 8 * t * t * t * t : 1 - 8 * (--t) * t * t * t),
  easeInBack:     (t) => t * t * (2.7 * t - 1.7),
  easeOutBack:    (t) => { const c = 1.70158; return 1 + (c + 1) * Math.pow(t - 1, 3) + c * Math.pow(t - 1, 2) },
  easeInOutBack:  (t) => { const c = 1.70158 * 1.525; return t < 0.5 ? (Math.pow(2*t,2)*((c+1)*2*t-c))/2 : (Math.pow(2*t-2,2)*((c+1)*(2*t-2)+c)+2)/2 },
  easeInElastic:  (t) => t === 0 ? 0 : t === 1 ? 1 : -Math.pow(2, 10*t-10) * Math.sin((t*10-10.75) * (2*Math.PI/3)),
  easeOutElastic: (t) => t === 0 ? 0 : t === 1 ? 1 : Math.pow(2, -10*t) * Math.sin((t*10-0.75) * (2*Math.PI/3)) + 1,
  easeOutBounce:  (t) => { if(t<1/2.75) return 7.5625*t*t; if(t<2/2.75){let u=t-1.5/2.75;return 7.5625*u*u+0.75}if(t<2.5/2.75){let u=t-2.25/2.75;return 7.5625*u*u+0.9375}let u=t-2.625/2.75;return 7.5625*u*u+0.984375 },
}

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t
}

function lerpVec3(a: Vec3, b: Vec3, t: number): Vec3 {
  return { x: lerp(a.x, b.x, t), y: lerp(a.y, b.y, t), z: lerp(a.z, b.z, t) }
}

export function getTotalDuration(preset: AnimationPreset): number {
  return preset.takes.reduce((max, t) => Math.max(max, t.endTime), 0)
}

export function getTakeAtTime(preset: AnimationPreset, time: number): { take: Take; index: number } {
  for (let i = 0; i < preset.takes.length; i++) {
    const t = preset.takes[i]
    if (time >= t.startTime && time < t.endTime) return { take: t, index: i }
  }
  const last = preset.takes.length - 1
  return { take: preset.takes[last], index: last }
}

function getTakeProgress(take: Take, time: number): number {
  const duration = take.endTime - take.startTime
  if (duration <= 0) return 1
  const elapsed = time - take.startTime
  return Math.max(0, Math.min(1, elapsed / duration))
}

// Calcula o estado de piscar da luz para um dado tempo dentro do take
function computeBlink(take: Take, timeInTake: number): boolean {
  const { blink } = take.lighting
  if (!blink.enabled || blink.interval <= 0) return true
  const blinkElapsed = timeInTake - blink.startOffset
  if (blinkElapsed < 0 || blinkElapsed > blink.duration) return true
  // Alterna a cada intervalo
  const step = Math.floor(blinkElapsed / blink.interval)
  return step % 2 === 0
}

// Calcula a opacidade do fade considerando transições entre takes
function computeFadeOpacity(preset: AnimationPreset, time: number): number {
  for (const take of preset.takes) {
    if (!take.fadeIn) continue
    // Usa fadeDuration do take se definido, senão usa o do preset
    const dur  = (take.fadeDuration ?? preset.fadeDuration) || 0.5
    const half = dur / 2
    const start = take.startTime

    // Fade out: escurece no final do take anterior
    if (time >= start - half && time < start) {
      const p = (time - (start - half)) / half
      return Math.min(1, p)
    }
    // Fade in: clareia no início deste take
    if (time >= start && time < start + half) {
      const p = (time - start) / half
      return Math.max(0, 1 - p)
    }
  }
  return 0
}

// Resolve todo o estado da câmera/luz/fade para um instante.
// A cena R3F apenas aplica o resultado — nenhuma lógica vive na cena.
export function resolveFrame(preset: AnimationPreset, time: number): ResolvedFrame {
  const { take, index } = getTakeAtTime(preset, time)
  const linear = getTakeProgress(take, time)
  const easingFn = easingFunctions[take.easing] ?? easingFunctions.easeInOut
  const eased = easingFn(linear)

  const position = lerpVec3(take.camera.start.position, take.camera.end.position, eased)
  const lookAt = lerpVec3(take.camera.start.lookAt, take.camera.end.lookAt, eased)
  const fov = lerp(take.camera.fovStart, take.camera.fovEnd, eased)

  const timeInTake = time - take.startTime
  const lightOn = computeBlink(take, timeInTake)
  const fadeOpacity = computeFadeOpacity(preset, time)

  return {
    takeIndex: index,
    takeProgress: linear,
    position,
    lookAt,
    fov,
    lightIntensity: take.lighting.intensity,
    lightColor: take.lighting.color,
    ambientIntensity: take.lighting.ambientIntensity,
    exposure: take.lighting.exposure,
    lightOn,
    fadeOpacity,
  }
}

// Validação básica ao carregar um preset de JSON
export function isValidPreset(data: unknown): data is AnimationPreset {
  if (!data || typeof data !== "object") return false
  const p = data as Partial<AnimationPreset>
  return (
    typeof p.name === "string" &&
    Array.isArray(p.takes) &&
    p.takes.every(
      (t) =>
        typeof t.startTime === "number" &&
        typeof t.endTime === "number" &&
        t.camera != null &&
        t.lighting != null,
    )
  )
}